/** V2 — Écrans du bloc PROJET. */
import { MATURITIES, DESTINATIONS, TYPES_BIEN, TYPE_INCONNU, DPE_VALUES, BIEN_IDENTIFIE } from "./simConstants";
import { BigButton, GhostButton, ChoiceCard, Chips, YesNo, StepTitle, SubLabel, AmountInput, CityInput } from "./SimPrimitives";

export function StepMaturity({ draft, patch, next }) {
  return (
    <div>
      <StepTitle emoji="🚀" title="Où en êtes-vous dans votre projet ?" />
      <div className="space-y-2.5">
        {MATURITIES.map((c) => (
          <ChoiceCard key={c.key} compact selected={draft.maturity === c.key} label={c.label} hint={c.hint}
            onClick={() => { patch({ maturity: c.key }); setTimeout(next, 160); }} testid={`sim-maturity-${c.key}`} />
        ))}
      </div>
    </div>
  );
}

export function StepDestination({ draft, patch, next }) {
  return (
    <div>
      <StepTitle emoji="🏡" title="Quel est votre projet ?" />
      <div className="space-y-3">
        {DESTINATIONS.map((c) => (
          <ChoiceCard key={c.key} selected={draft.destination === c.key} emoji={c.emoji} label={c.label}
            onClick={() => { patch({ destination: c.key }); setTimeout(next, 160); }} testid={`sim-destination-${c.key}`} />
        ))}
      </div>
    </div>
  );
}

export function StepTypeBien({ draft, patch, next }) {
  const opts = draft.maturity === "recherche" ? [...TYPES_BIEN, TYPE_INCONNU] : TYPES_BIEN;
  return (
    <div>
      <StepTitle emoji="🏗️" title="Quel type de bien ?" />
      <div className="space-y-3">
        {opts.map((c) => (
          <ChoiceCard key={c.key} selected={draft.type_bien === c.key} emoji={c.emoji} label={c.label}
            onClick={() => { patch({ type_bien: c.key }); setTimeout(next, 160); }} testid={`sim-type_bien-${c.key}`} />
        ))}
      </div>
    </div>
  );
}

export function StepLocalisationProjet({ draft, patch, next }) {
  const bien = BIEN_IDENTIFIE.has(draft.maturity);
  const title = bien ? "Où se situe le bien ?" : draft.maturity === "recherche" ? "Dans quel secteur cherchez-vous ?" : "Dans quel secteur envisagez-vous d'acheter ?";
  const ok = !!draft.projet_ville;
  return (
    <div>
      <StepTitle emoji="📍" title={title} hint={bien ? "Ville ou code postal du bien" : "Facultatif — une ville ou un code postal suffit"} />
      <CityInput ville={draft.projet_ville} codePostal={draft.projet_code_postal} testid="sim-projet-ville"
        onChange={({ ville, code_postal }) => patch({ projet_ville: ville, projet_code_postal: code_postal })} />
      <div className="mt-8 space-y-2">
        <BigButton onClick={next} disabled={bien && !ok} testid="sim-localisation_projet-next">Suivant →</BigButton>
        {!bien && <GhostButton onClick={() => { patch({ projet_ville: "", projet_code_postal: "" }); next(); }} testid="sim-localisation_projet-skip">Passer</GhostButton>}
      </div>
    </div>
  );
}

export function StepPrix({ draft, patch, next, cible }) {
  const key = cible ? "prix_cible" : "prix_bien";
  const v = draft[key];
  return (
    <div>
      <StepTitle emoji="💶" title={cible ? "Quel budget visez-vous ?" : "Quel est le prix du bien ?"} hint={cible ? "Facultatif — un ordre de grandeur suffit" : "Hors frais de notaire"} />
      <AmountInput value={v} onChange={(x) => patch({ [key]: x })} testid={`sim-${key}-input`} autoFocus />
      <div className="mt-8 space-y-2">
        <BigButton onClick={next} disabled={!cible && Number(v) <= 0} testid={`sim-${key}-next`}>Suivant →</BigButton>
        {cible && <GhostButton onClick={() => { patch({ prix_cible: "" }); next(); }} testid="sim-prix_cible-skip">Passer</GhostButton>}
      </div>
    </div>
  );
}

export function StepTravaux({ draft, patch, next }) {
  const ok = draft.travaux_prevus === false || (draft.travaux_prevus === true && Number(draft.travaux_montant) > 0);
  return (
    <div>
      <StepTitle emoji="🔨" title="Des travaux sont-ils prévus ?" />
      <YesNo value={draft.travaux_prevus} testid="sim-travaux" onChange={(v) => { patch({ travaux_prevus: v }); if (v === false) setTimeout(next, 160); }} />
      {draft.travaux_prevus === true && (
        <>
          <SubLabel>Montant estimé des travaux</SubLabel>
          <AmountInput value={draft.travaux_montant} onChange={(x) => patch({ travaux_montant: x })} testid="sim-travaux-montant-input" autoFocus />
          <div className="mt-8"><BigButton onClick={next} disabled={!ok} testid="sim-travaux-next">Suivant →</BigButton></div>
        </>
      )}
    </div>
  );
}

export function StepDpe({ draft, patch, next }) {
  return (
    <div>
      <StepTitle emoji="⚡" title="Connaissez-vous le DPE du bien ?" hint="Il figure sur l'annonce. Sinon, passez." />
      <div className="grid grid-cols-7 gap-1.5 mb-4">
        {DPE_VALUES.map((k) => (
          <button key={k} type="button" onClick={() => { patch({ dpe: k }); setTimeout(next, 160); }} data-testid={`sim-dpe-${k}`} aria-pressed={draft.dpe === k}
            className={`min-h-[52px] rounded-xl border-2 font-extrabold text-lg sim-chip ${draft.dpe === k ? "border-[color:var(--cf-primary)] bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary-text)]" : "border-slate-200 bg-white text-slate-700"}`}>{k}</button>
        ))}
      </div>
      <ChoiceCard compact selected={draft.dpe === "inconnu"} emoji="🤷" label="Je ne sais pas" onClick={() => { patch({ dpe: "inconnu" }); setTimeout(next, 160); }} testid="sim-dpe-inconnu" />
    </div>
  );
}

export function StepDatesCompromis({ draft, patch, next }) {
  const ok = !!draft.compromis_date && (draft.cs_inconnue || !!draft.condition_suspensive_date);
  const inp = "w-full rounded-2xl bg-white border-2 border-slate-200 focus:border-[color:var(--cf-primary)] p-3 text-[17px] font-bold text-slate-900 outline-none";
  return (
    <div>
      <StepTitle emoji="📝" title="Votre compromis" />
      <SubLabel>Date de signature du compromis</SubLabel>
      <input type="date" value={draft.compromis_date} onChange={(e) => patch({ compromis_date: e.target.value })} className={inp} data-testid="sim-compromis-date" />
      <SubLabel>Date limite d'obtention du financement (condition suspensive)</SubLabel>
      {!draft.cs_inconnue && <input type="date" value={draft.condition_suspensive_date} onChange={(e) => patch({ condition_suspensive_date: e.target.value })} className={inp} data-testid="sim-cs-date" />}
      <label className="flex items-center gap-3 mt-3 cursor-pointer" data-testid="sim-cs-inconnue">
        <input type="checkbox" checked={draft.cs_inconnue} onChange={(e) => patch({ cs_inconnue: e.target.checked, condition_suspensive_date: "" })} className="w-5 h-5 accent-[color:var(--cf-primary)]" />
        <span className="text-[13px] font-semibold text-slate-700">Je ne la connais pas</span>
      </label>
      <div className="mt-8"><BigButton onClick={next} disabled={!ok} testid="sim-dates_compromis-next">Suivant →</BigButton></div>
    </div>
  );
}

export function StepApport({ draft, patch, next }) {
  const prix = Number(draft.prix_bien) || 0;
  const neuf = draft.type_bien === "neuf";
  return (
    <div>
      <StepTitle emoji="🏦" title="Quel apport souhaitez-vous consacrer à votre projet ?" />
      <AmountInput value={draft.apport} onChange={(x) => patch({ apport: x })} testid="sim-apport-input" autoFocus />
      {prix > 0 && (
        <div className="mt-3 rounded-xl bg-blue-50 border border-blue-100 p-3 text-[12.5px] text-slate-700 leading-relaxed" data-testid="sim-apport-pedago">
          À titre indicatif, un apport d'environ <strong>{neuf ? "5 %" : "10 %"}</strong> du prix du bien permet généralement de couvrir une grande partie des frais liés à l'acquisition.
        </div>
      )}
      <SubLabel>Après cet apport, il vous restera… <span className="text-slate-400 font-semibold">(facultatif)</span></SubLabel>
      <Chips options={[{ key: "rien", label: "Quasiment rien" }, { key: "petite", label: "Une petite réserve" }, { key: "confortable", label: "Une réserve confortable" }]}
        value={draft.epargne_residuelle} onChange={(k) => patch({ epargne_residuelle: k })} testid="sim-epargne" />
      <div className="mt-8"><BigButton onClick={next} disabled={draft.apport === ""} testid="sim-apport-next">Voir mon indice de faisabilité →</BigButton></div>
    </div>
  );
}
